/**
 * The audio manifest — every clip the game ships, with the facts about it
 * that the code needs and the facts a human needs before replacing one.
 *
 * ─── Paths are extension-less on purpose ────────────────────────────────────
 * Each entry names `/audio/music/pvp-stealth`, and `mixer.load` appends
 * `.ogg` or `.m4a` after asking the browser which it can decode. Both files
 * exist for every entry and are cut from the same master, so the choice is
 * purely a codec-support question and no caller needs to think about it.
 *
 * ─── Every clip here is a verified seamless loop ────────────────────────────
 * They are not raw generator output. Each was loudness-normalised with a
 * single constant gain (two-pass EBU R128, `linear=true`) and then tail-
 * wrapped: the last N seconds were crossfaded over the first N, which makes
 * the last sample and the first sample originally-adjacent ones. The seam was
 * then measured on the shipped Opus bytes — the wrap step sits in the 5th to
 * 91st percentile of the steps already inside each file, i.e. the join is
 * quieter than the material's own normal sample-to-sample motion.
 *
 * If you replace a file, re-run that wrap. Dropping an unprocessed clip in
 * here will loop with a click, and the click will be blamed on the mixer.
 */

export interface TrackDef {
  /** Extension-less public path — see the header. */
  path: string;
  /** Which mixer bus, and therefore which slider in the Sound tab. */
  channel: 'music' | 'effects';
  /** Loop length in seconds, after the tail-wrap. */
  seconds: number;
  /** Integrated loudness of the shipped file, LUFS. */
  lufs: number;
  /**
   * How it was made and what was done to it, so the next person can match it.
   *
   * Deliberately generic — no supplier, tool or delivered file name. Janeks:
   * *"cannot contain brand names, pure technical label on sounds (and
   * pictures)."* What matters downstream is the treatment (tail-wrap, trim,
   * normalisation) and the brief it was cut against, not who rendered it.
   */
  source: string;
  /**
   * True for a stinger — played once, never looped.
   *
   * Worth a flag rather than a convention: every other entry here is a
   * tail-wrapped loop, so `playLoop` on a one-shot is a plausible mistake,
   * and it would fold the clip's tail onto its head and destroy the arc that
   * makes it a reward. One-shots are also mixed LOUDER than the beds, which
   * the asset test needs to know before it can judge either.
   */
  oneShot?: true;
  /**
   * True peak of the shipped file, dBFS. Recorded for one-shots only.
   *
   * Because LUFS is the wrong yardstick for a transient. A gunshot is
   * PEAK-limited, not loudness-limited: normalising this one towards -12 LUFS
   * ran it into the -3 dBFS ceiling first and stopped there, leaving it at
   * -22.2 LUFS integrated — a number that says "quiet" about a sound that
   * cracks. Its crest factor is the point of it. So a stinger's audibility is
   * judged on peak, and only a bed's on integrated loudness.
   */
  peakDbfs?: number;
}

export const TRACKS = {
  /**
   * The settlement bed. Deepest and quietest of the three scores because it
   * plays for the longest stretch of any session — this is the one a player
   * hears while doing inventory admin, so it sits under everything.
   * Measured: 73% of its energy below 250 Hz, spectral centroid 209 Hz, and
   * nothing at all above 2 kHz.
   */
  settlementAmbient: {
    path: '/audio/music/settlement-ambient',
    channel: 'music',
    seconds: 56.03,
    lufs: -19.7,
    source: 'generated bed, 60 s, tail-wrapped 4 s',
  },

  /**
   * PvP, before the opponent has seen you. Deeper and quieter still
   * (centroid 161 Hz, 86% below 250 Hz) so that the action track reads as an
   * increase without either of them being loud.
   *
   * NOT WIRED. Deliberately: the switch from this to `pvpAction` needs to
   * know when the player has been spotted, and the PvP state broadcast does
   * not carry that — `OpponentStateView` in `battleground.types.ts` has
   * position, facing and hp and nothing else. Deriving "seen" on the client
   * would mean inventing a detection rule (range? facing cone? line of
   * sight?) that the server does not share, in a server-authoritative game,
   * and two clients would disagree about when the music changed. It needs a
   * `spotted` flag on the server's PvP broadcast first.
   */
  pvpStealth: {
    path: '/audio/music/pvp-stealth',
    channel: 'music',
    seconds: 41.08,
    lufs: -21.0,
    source: 'generated bed, 45 s, tail-wrapped 4 s',
  },

  /**
   * PvP, once you have been seen. Urgency comes from movement in the low
   * strings rather than from brightness — only 4.8% of its energy is above
   * 2 kHz, which is what keeps it off the "harsh" end while still lifting
   * against the stealth bed.
   *
   * Authored with no strong beat grid, which is a loop decision as much as a
   * tonal one: the wrap point cannot land on a downbeat if there are no
   * downbeats, so it never stumbles rhythmically on the lap.
   *
   * NOT WIRED — same reason as `pvpStealth`.
   */
  pvpAction: {
    path: '/audio/music/pvp-action',
    channel: 'music',
    seconds: 42.57,
    lufs: -19.0,
    source: 'generated bed, 45 s, tail-wrapped 2.5 s',
  },

  /**
   * Level-up stinger. The only one-shot here — everything else is a bed.
   *
   * Shaped to Janeks's brief rather than left to the generator: the first hit
   * is low and restrained, the ambience and melody build to a peak in the
   * MIDDLE, then tame down. Measured in ten slices of the shipped file, the
   * RMS envelope runs -62.9, -46.9, -36.3, -27.5, -27.6, **-14.4 (peak)**,
   * -15.4, -16.6, -20.0, -21.7 dBFS — a genuine mid-peak arc, not the
   * front-loaded decay a first take produced.
   *
   * Louder than the beds on purpose (-13.9 LUFS against their -19 to -21): a
   * reward stinger that does not rise above the score it plays over is not a
   * reward. It is on `effects` so the Effects slider governs it.
   *
   * NOT loop-wrapped — wrapping a one-shot would fold its tail onto its head
   * and destroy exactly the arc above. It ends on a 120 ms fade so stopping
   * cannot click.
   */
  levelUp: {
    path: '/audio/sfx/level-up',
    channel: 'effects',
    seconds: 4.02,
    lufs: -13.9,
    source: 'generated one-shot, 4 s (take 2 of 2 — take 1 peaked at 0.4 s)',
    oneShot: true,
    peakDbfs: -5.4,
  },

  /**
   * The raid-start sting. Two generated layers premixed into one file: war
   * drums underneath, then a canvas pack being shouldered and a zipper pulled
   * shut, entering 0.3 s later. Premixed so the mixer plays one clip and the
   * layers can never drift apart. Fires once, on a raid launched this session.
   */
  raidStart: {
    path: '/audio/sfx/raid-start',
    channel: 'effects',
    seconds: 4.10,
    lufs: -18.4,
    source: 'two generated layers (drums; pack and zipper), 0.3 s offset, loudness-normalised, 4 s one-shot',
    oneShot: true,
    peakDbfs: -5.9,
  },

  /**
   * Pistol shot. Supplied by Janeks, trimmed, peak-normalised and given an
   * 80 ms tail fade.
   *
   * Fires for every pistol-ammo weapon — sidearms AND submachine guns, which
   * is the whole reason `weapon-sound.ts` keys one rule on `ammoType` instead
   * of listing ten weapon keys.
   *
   * This comment used to say `pistol` was the ONLY sounded class, and that
   * `ammoType` could be `shotgun`. Neither is true any more: the catalog work
   * collapsed ammo to `pistol` / `rifle` / `heavy`, and rifle, sniper,
   * machinegun and shotgun reports have all since landed.
   *
   * See `peakDbfs` on the interface for why this sits at -22.2 LUFS and is
   * still the loudest thing in the game.
   */
  gunshotPistol: {
    path: '/audio/sfx/gunshot-pistol',
    channel: 'effects',
    seconds: 1.48,
    lufs: -22.2,
    source: 'supplied one-shot, briefed as "pistol shot"; trimmed + peak-normalised',
    oneShot: true,
    peakDbfs: -3.2,
  },

  /**
   * Campfire. Short by design — fire crackle is stationary, so a 5s loop is
   * indistinguishable from a 30s one and costs a twentieth of the bytes.
   * Supplied by Janeks rather than generated; normalised and wrapped here.
   */
  campfire: {
    path: '/audio/sfx/campfire-loop',
    channel: 'effects',
    seconds: 5.49,
    lufs: -18.2,
    source: 'supplied loop, briefed as "campfire"; tail-wrapped 1 s',
  },

  /**
   * Rifle shot — the `carbine`.
   *
   * The quietest source of the batch by far (-44.7 LUFS, peaking at -19.6
   * dBFS), so it took +16.7 dB of straight gain to reach the same peak as the
   * others. That gain lifts its noise floor with it; if it ever sounds hissy
   * next to the pistol, that is why, and the fix is a louder recording rather
   * than more processing here.
   */
  gunshotRifle: {
    path: '/audio/sfx/gunshot-rifle',
    channel: 'effects',
    seconds: 1.48,
    lufs: -28.2,
    source: 'supplied one-shot, briefed as "rifle shot"',
    oneShot: true,
    peakDbfs: -2.9,
  },

  /**
   * Sniper shot — `frf2` and `rifle_bolt`, per Janeks: *"f2f, bolt rifle are
   * snipers"*. The delivered file's own name called it a heavy machine gun
   * and Janeks' brief called it a sniper rifle shot; the two disagree and the
   * BRIEF is what was followed. Recorded here so the next person sees the
   * contradiction rather than rediscovering it by ear.
   *
   * CLIPPED AT SOURCE: 15 samples pinned to full scale with a longest flat
   * run of 7 (~0.15 ms). That is real flat-topping, not intersample
   * overshoot, so the distortion survives any amount of gain staging — it is
   * in the samples. On a gunshot transient it is very unlikely to be audible,
   * but it is there and it cannot be processed out.
   *
   * Because those flat tops reconstruct badly, this one is normalised well
   * below the -3 dBFS sample peak the clean recordings get. It took two
   * passes to get there, and the second one is the interesting one:
   *
   *  - At -3 the **Opus** file came back at +0.1 dBTP. Dropped to -6.
   *  - At -6 the Opus was fine (-2.9) but the **AAC** was still at +0.2 dBTP,
   *    and it shipped that way — the first pass measured only the .ogg. The
   *    .m4a is the file Safari gets and nothing else does, which is exactly
   *    the asset least likely to be caught by the person who broke it. Found
   *    while measuring the shotgun batch; re-cut another 2 dB down.
   *
   * Both encodings are now measured for every one-shot here, not just the
   * Opus. See the README's "Measure BOTH encodings" section.
   */
  gunshotSniper: {
    path: '/audio/sfx/gunshot-sniper',
    channel: 'effects',
    seconds: 1.48,
    lufs: -18.8,
    source: 'supplied one-shot, briefed as "sniper rifle shot"; clipped at source',
    oneShot: true,
    peakDbfs: -4.8,
  },

  /**
   * Machinegun shot.
   *
   * Mapped to `m249` and `pkm`, per Janeks: *"Machine guns are m249 and
   * PKM"*. Both are frontend-only models today — registered in
   * `WEAPON_MODEL_BY_KEY` with grips and scale, but with no backend item row —
   * so nothing can equip and fire one YET. The mapping is by the key they
   * will carry when the row lands, so the sound is right on day one instead
   * of being something someone has to remember.
   *
   * It briefly hung off `heavy` ammo instead, which was wrong twice over: the
   * m249 and PKM are 5.56 and 7.62, and the only .50 weapon in the registry
   * is the `m82`, a sniper.
   *
   * Same source clipping as the sniper — 27 samples at full scale, longest
   * flat run 8 — and the same -6 dBFS normalisation for the same reason.
   */
  gunshotMachinegun: {
    path: '/audio/sfx/gunshot-machinegun',
    channel: 'effects',
    seconds: 1.48,
    lufs: -19.8,
    source: 'supplied one-shot, briefed as "machinegun shot"; clipped at source',
    oneShot: true,
    peakDbfs: -2.6,
  },

  /**
   * Shotgun blast, supplied by Janeks.
   *
   * This closes the one real gap the gunshot table had. `shotgun`,
   * `auto_shotgun` and `sawed_off` are all `ammoType: 'rifle'` and were all
   * firing the RIFLE report as a documented stand-in — audibly wrong, chosen
   * over three silent guns. They now key to this instead, through
   * `SOUNDED_WEAPON` rather than `SOUNDED_AMMO`, because their ammo type is
   * shared with every assault rifle in the game and only the item key can
   * separate them.
   *
   * Shorter than the other reports (1.08 s against 1.48 s) because that is
   * where the recording ends; its tail is already at -78 dBFS by then, so
   * nothing was cut. Clipped at source like the rest of this batch — see the
   * README — 532 samples at full scale, longest flat run 0.67 ms.
   */
  gunshotShotgun: {
    path: '/audio/sfx/gunshot-shotgun',
    channel: 'effects',
    seconds: 1.08,
    lufs: -17.6,
    source: 'supplied one-shot, briefed as "shotgun shot"; peak-normalised; clipped at source',
    oneShot: true,
    peakDbfs: -3.0,
  },

  /**
   * Taking a hit from a melee weapon. It and `uiBuy` above arrived in
   * deliveries whose file names differed only by a timestamp, and they are
   * DIFFERENT recordings — telling them apart is what the `source` field is
   * for.
   *
   * Mixed BELOW the gunshots (-7.3 dBTP against their -2.6…-3.3). The player's
   * own weapon is the loudest thing in the game; damage feedback has to be
   * unmissable without competing with it, or a firefight turns into two
   * transients fighting for the same headroom.
   *
   * Quieter than `hitBullet` by ~2.7 dB, which is the one place this batch's
   * levels are not arbitrary: a bullet impact being sharper than a club is
   * the right way round.
   *
   * The worst-clipped file of a badly clipped batch after `hitBullet` — 290
   * samples at full scale with a 2.06 ms flat run. Its encoded peak is also
   * NOT a monotonic function of its sample peak (a -8 dBFS cut encoded HOTTER
   * than a -9 one), which is what that much flat-topping does to an encoder.
   * Chosen by measuring, not by arithmetic.
   */
  hitMelee: {
    path: '/audio/sfx/hit-melee',
    channel: 'effects',
    seconds: 0.68,
    lufs: -23.7,
    source: 'supplied one-shot, briefed as "taking damage by a melee weapon"; clipped at source',
    oneShot: true,
    peakDbfs: -7.3,
  },

  /**
   * Taking a hit from gunfire.
   *
   * **The last 0.2 s were cut**, at Janeks's instruction. Worth recording why
   * it was a good call rather than just that it was made: the source's
   * ten-slice RMS envelope ends -46.2, -57.2, -61.4, **-44.6** — it decays and
   * then comes back up, so the recording had a second event tacked onto the
   * end of the impact. 0.68 s -> 0.48 s removes it. The cut lands where the
   * signal is at -58 dBFS, so the 60 ms tail fade over it is inaudible; a cut
   * into a hot decay is the thing that would have clicked.
   *
   * The most clipped file in the batch: 512 samples at full scale and a
   * **5.98 ms** flat run. That is not a transient artefact, it is a stretch of
   * hard square wave in the middle of the impact, and no processing removes
   * it — see the README.
   */
  hitBullet: {
    path: '/audio/sfx/hit-bullet',
    channel: 'effects',
    seconds: 0.48,
    lufs: -20.2,
    source: 'supplied one-shot, briefed as "taking damage from bullets"; last 0.2 s cut per instruction; clipped at source',
    oneShot: true,
    peakDbfs: -4.6,
  },

  /**
   * Swinging a melee weapon.
   *
   * Mixed down near the interface tier (-9.4 dBTP, between `uiCredits` at -6.4
   * and `uiClick` at -11.7) rather than at gunshot level, for the reason
   * `uiClick` is quiet: it is the player's own repeatable action, fired from
   * the same unrate-limited pointer handler as the muzzle flash. A whoosh at
   * blast level is how a player ends up turning effects off.
   *
   * It is also physically the right way round — a swing displaces air, a
   * cartridge does not.
   */
  swingMelee: {
    path: '/audio/sfx/swing-melee',
    channel: 'effects',
    seconds: 0.68,
    lufs: -26.9,
    source: 'supplied one-shot, briefed as "melee weapon swing sound"; clipped at source',
    oneShot: true,
    peakDbfs: -9.4,
  },

  /**
   * UI click — buttons and dialogue-tree choices.
   *
   * Mixed FAR lower than anything else here (-11.7 dBTP against the gunshots'
   * -2.9) because it is the only sound in the game that can fire many times a
   * minute for an entire session. A click at combat level is the fastest way
   * to make a player turn effects off altogether.
   */
  uiClick: {
    path: '/audio/sfx/ui-click',
    channel: 'effects',
    seconds: 0.48,
    lufs: -33.6,
    source: 'supplied one-shot, briefed as "dialogue tree and button clicks"',
    oneShot: true,
    peakDbfs: -11.7,
  },

  /**
   * A pencil drawing one circle — played when a raid zone is picked on the map,
   * in step with the red pencil circle being drawn around it.
   *
   * Held at the interface tier (-10.8 dBTP, a little above `uiClick`) and not
   * louder: it is the player's own repeatable action, and a scratchy sound is
   * the first one to wear. Trimmed from a longer take to the single stroke, sped up 2x so it
   * lasts about as long as the 0.45 s draw, and given a 150 ms fade out, so it neither clicks on nor
   * trails off into hiss.
   */
  uiPencil: {
    path: '/audio/sfx/ui-pencil',
    channel: 'effects',
    seconds: 0.65,
    lufs: -27.9,
    source: 'generated one-shot, briefed as "a pencil drawing a single circle on paper"; trimmed to the stroke, sped up 2x to match the quick draw, gain-matched to the interface tier',
    oneShot: true,
    peakDbfs: -10.9,
  },

  /**
   * Credits gained — vendor sale, black-market exchange, quest reward.
   *
   * One sound for all three because they are one event from the player's
   * side: the balance went up. `credits-sound.ts` watches `softBalance`
   * rather than hooking each call site, so a fourth way to earn money is
   * covered the day it ships without anyone remembering to wire it.
   */
  uiCredits: {
    path: '/audio/sfx/ui-credits',
    channel: 'effects',
    seconds: 0.48,
    lufs: -21.5,
    source: 'supplied one-shot, briefed as "every time the player gets ++credits"',
    oneShot: true,
    peakDbfs: -6.4,
  },

  /**
   * Error. The delivered file's own name said "buy" and Janeks' brief said
   * "error sound" — name and brief disagree, BRIEF followed. See
   * `gunshotSniper` for the other one of these.
   */
  uiError: {
    path: '/audio/sfx/ui-error',
    channel: 'effects',
    seconds: 0.80,
    lufs: -30.1,
    source: 'supplied one-shot, briefed as "error sound" (its own file name said "buy" — the brief was followed)',
    oneShot: true,
    peakDbfs: -7.0,
  },

  /**
   * Item bought — briefed as "item buy sound", against a delivered file name
   * that said otherwise. Again: brief followed.
   */
  uiBuy: {
    path: '/audio/sfx/ui-buy',
    channel: 'effects',
    seconds: 0.88,
    lufs: -29.9,
    // Called out on purpose: `hitMelee` below arrived under a file name that
    // differed from this one only by its timestamp, and it is a different
    // recording. Two entries that read as the same source is the confusion
    // this field exists to prevent.
    source: 'supplied one-shot, briefed as "item buy sound" (an earlier delivery than the melee-hit take, same file-name prefix)',
    oneShot: true,
    peakDbfs: -6.1,
  },
} as const satisfies Record<string, TrackDef>;

export type TrackId = keyof typeof TRACKS;
